import { config } from '@/config/env'
import type { SkipNetworkErrorInit } from '@/clientWeb/components/ui/network-error-dialog'

/**
 * TURN credentials fetched from the backend, per join.
 *
 * The shared coturn deployment runs `use-auth-secret`, so a credential is
 * derived from a secret rather than stored: the username IS an expiry
 * timestamp and the credential is an HMAC of it. That has two consequences for
 * this client:
 *
 *  - the secret must never be here. It cannot be a `VITE_*` variable, because
 *    those are compiled into this bundle and served to everyone.
 *  - credentials cannot be built once at module load. The username is the
 *    expiry, so a value computed at startup is rejected by coturn after the
 *    TTL — which surfaces as a call that connects for early joiners and fails
 *    for anyone who joins later in the session.
 *
 * So this is called at join time, every time.
 */

export interface TurnCredentials {
  urls: string[]
  username: string
  credential: string
  ttl: number
}

/**
 * The app's access token, read the same way the other meeting services read it
 * (`recordings.api.ts`). The backend authenticates with a Bearer header, not
 * the session cookie — the cookie is scoped to `/api/v1/auth` and is only used
 * to mint access tokens.
 */
function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } })
      ?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

/**
 * How long the TURN fetch may take before the join gives up on it.
 *
 * It had no limit. It runs inside every SFU join, so a request stalled on a
 * connection the network change left half-dead held the whole join — no
 * transports, no error, no retry — while the meeting sat at "connecting".
 * Timing out is the same outcome as the backend failing: STUN alone.
 */
export const TURN_FETCH_TIMEOUT_MS = 8_000

/**
 * Fetches short-lived TURN credentials.
 *
 * Returns `null` when the backend has no TURN configured (503), the call
 * fails, or it takes longer than TURN_FETCH_TIMEOUT_MS. The caller falls back
 * to STUN alone: direct connections still work, only participants who need a
 * relay are affected — which is a better outcome than failing the join outright.
 */
export async function fetchTurnCredentials(signal?: AbortSignal): Promise<TurnCredentials | null> {
  const token = appToken()
  if (!token) return null

  // AbortSignal.any / AbortSignal.timeout are too new for every browser this
  // ships to, so the caller's signal and the deadline share one controller.
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TURN_FETCH_TIMEOUT_MS)
  const onCallerAbort = () => ctrl.abort()
  if (signal?.aborted) ctrl.abort()
  else signal?.addEventListener('abort', onCallerAbort, { once: true })

  try {
    const res = await fetch(`${config.apiUrl}/api/v1/turn-credentials`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      signal: ctrl.signal,
      // A failure here is handled — STUN alone — so it must not black out the app.
      skipNetworkError: true,
    } as SkipNetworkErrorInit)
    if (!res.ok) return null

    const data = (await res.json()) as Partial<TurnCredentials>
    if (!Array.isArray(data.urls) || !data.urls.length || !data.username || !data.credential) {
      return null
    }
    return {
      urls: data.urls,
      username: data.username,
      credential: data.credential,
      ttl: typeof data.ttl === 'number' ? data.ttl : 0,
    }
  } catch {
    return null
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onCallerAbort)
  }
}

/**
 * ICE servers for an RTCPeerConnection: STUN from build config, TURN minted
 * per call. Both `urls` arrays are passed as-is — the browser tries each
 * transport in turn, so udp/tcp/tls variants belong in one entry.
 */
export async function buildIceServers(signal?: AbortSignal): Promise<RTCIceServer[]> {
  const iceServers: RTCIceServer[] = [{ urls: config.ice.stunUrl }]

  const turn = await fetchTurnCredentials(signal)
  if (turn) {
    iceServers.push({
      urls: turn.urls,
      username: turn.username,
      credential: turn.credential,
    })
  }
  return iceServers
}
